import { sendViewJobDislikeRequest } from "./sendViewJobDislikeRequest.web";
import {
  getStoredViewJobDislikeState,
  setStoredViewJobDislikeState,
} from "./viewJobDislikeStorage.web";
import { useI18n } from "../../../../../../shared/I18n";
import {
  dispatchDislikedJobStateChanged,
  subscribeDislikedJobStateChanged,
} from "../../../../../../shared/native-modules/tare/dislikedJobStateChangedEvent";
import { subscribeSavedJobStateChanged } from "../../../../../../shared/native-modules/tare/savedJobStateChangedEvent";
import {
  getCachedViewJobHint,
  updateViewJobHint,
} from "../../../../../../shared/native-modules/tare/viewJobHint";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import useToggleSaveJob from "../SaveJobButton/useToggleSaveJob";

import {
  type dislikeButtonClick,
  log as logDislikeButtonClick,
  PageId,
  Source,
} from "@indeed/signals-schema/com/indeed/signals/schema/jobsearch/dislikeButtonClick";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessibilityInfo } from "react-native";

type UseViewJobDislikeParams = {
  jobKey: string;
  trackingKey?: string;
  initialIsSaved: boolean;
};

type UseViewJobDislikeResult = {
  isDisliked: boolean;
  isPending: boolean;
  onPressDislike: () => void;
};

export function useViewJobDislike({
  jobKey,
  trackingKey,
  initialIsSaved,
}: UseViewJobDislikeParams): UseViewJobDislikeResult {
  const i18n = useI18n();
  const viewJobDisplay = useRnViewJobDisplay();
  const { toggle: toggleSaveJob } = useToggleSaveJob(jobKey);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isSaved, setIsSaved] = useState(initialIsSaved);
  const stateVersionRef = useRef(0);
  const mountedRef = useRef(true);
  const currentJobKeyRef = useRef(jobKey);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentJobKeyRef.current = jobKey;
    const initialStateVersion = ++stateVersionRef.current;
    let cancelled = false;

    setIsDisliked(false);
    setIsPending(false);
    Promise.all([getStoredViewJobDislikeState(jobKey), getCachedViewJobHint(jobKey)])
      .then(([storedState, cachedHint]) => {
        if (!cancelled && stateVersionRef.current === initialStateVersion) {
          setIsDisliked(storedState ?? cachedHint?.isDisliked ?? false);
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to load ViewJob dislike state", error);
      });

    return () => {
      cancelled = true;
    };
  }, [jobKey]);

  useEffect(() => {
    setIsSaved(initialIsSaved);
  }, [initialIsSaved, jobKey]);

  useEffect(() => {
    const subscription = subscribeDislikedJobStateChanged((event) => {
      if (event.jobKey !== jobKey || event.source === "viewjob") {
        return;
      }

      stateVersionRef.current += 1;
      setIsDisliked(event.isDisliked);
      void updateViewJobHint(jobKey, { isDisliked: event.isDisliked }).catch((error: unknown) => {
        console.error("Failed to cache external ViewJob dislike state", error);
      });
    });

    return () => {
      subscription.remove();
    };
  }, [jobKey]);

  useEffect(() => {
    const subscription = subscribeSavedJobStateChanged((event) => {
      if (event.jobKey === jobKey) {
        setIsSaved(event.isSaved);
      }
    });

    return () => {
      subscription.remove();
    };
  }, [jobKey]);

  const performDislikeToggle = useCallback(async () => {
    if (isPending) {
      return;
    }

    const previousIsDisliked = isDisliked;
    const nextIsDisliked = !previousIsDisliked;
    const isEmbedded = viewJobDisplay === "DESKTOP_EMBEDDED";
    const logEvent: dislikeButtonClick = {
      source: {
        Source: isEmbedded ? Source.TWO_PANE : Source.STANDALONE,
      },
      pageId: {
        PageId: PageId.VIEWJOB,
      },
    };

    stateVersionRef.current += 1;
    setIsPending(true);
    setIsDisliked(nextIsDisliked);
    logDislikeButtonClick(logEvent);

    try {
      await updateViewJobHint(jobKey, { isDisliked: nextIsDisliked }).catch((error: unknown) => {
        console.error("Failed to cache ViewJob dislike state", error);
      });

      if (isEmbedded) {
        dispatchDislikedJobStateChanged({
          eventType: "DislikedJobStateChanged",
          jobKey,
          isDisliked: nextIsDisliked,
          source: "viewjob",
          ...(trackingKey ? { vjtk: trackingKey } : {}),
        });
      } else {
        await setStoredViewJobDislikeState(jobKey, nextIsDisliked);

        if (nextIsDisliked && isSaved) {
          try {
            const saveResult = await toggleSaveJob("SAVED");
            setIsSaved(saveResult.isSaved);
            await updateViewJobHint(jobKey, { isSaved: saveResult.isSaved });
          } catch (error: unknown) {
            console.error("Failed to unsave disliked ViewJob", error);
          }
        }

        void sendViewJobDislikeRequest({
          jobKey,
          isUndo: !nextIsDisliked,
          trackingKey,
        }).catch((error: unknown) => {
          console.error("Failed to send ViewJob dislike request", error);
        });
      }

      AccessibilityInfo.announceForAccessibility(
        nextIsDisliked ? i18n.gettext("Job hidden") : i18n.gettext("Job restored"),
      );
    } catch (error: unknown) {
      stateVersionRef.current += 1;
      if (mountedRef.current && currentJobKeyRef.current === jobKey) {
        setIsDisliked(previousIsDisliked);
      }
      const rollbackOperations: Array<Promise<unknown>> = [
        updateViewJobHint(jobKey, { isDisliked: previousIsDisliked }),
      ];
      if (!isEmbedded) {
        rollbackOperations.push(setStoredViewJobDislikeState(jobKey, previousIsDisliked));
      }
      await Promise.allSettled(rollbackOperations);
      console.error("Failed to update ViewJob dislike state", error);
      AccessibilityInfo.announceForAccessibility(i18n.gettext("Unable to update job preference"));
    } finally {
      if (mountedRef.current && currentJobKeyRef.current === jobKey) {
        setIsPending(false);
      }
    }
  }, [i18n, isDisliked, isPending, isSaved, jobKey, toggleSaveJob, trackingKey, viewJobDisplay]);

  const onPressDislike = useCallback(() => {
    void performDislikeToggle();
  }, [performDislikeToggle]);

  return {
    isDisliked,
    isPending,
    onPressDislike,
  };
}
